import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnFiltersState,
  type SortingState,
  type VisibilityState,
} from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { Button, EmptyState, Input, Spinner } from "@/components/ui";
import { downloadCsv } from "@/lib/format";
import { cn } from "@/lib/cn";

export interface DataTableProps<TData extends Record<string, unknown>> {
  data: TData[];
  columns: ColumnDef<TData, unknown>[];
  isLoading?: boolean;
  error?: string | null;
  searchPlaceholder?: string;
  searchColumnId?: string;
  exportFilename?: string;
  emptyTitle?: string;
  emptyDescription?: string;
  pageSize?: number;
  /** Render inside a parent panel (no extra card border around the table). */
  embedded?: boolean;
}

export function DataTable<TData extends Record<string, unknown>>({
  data,
  columns,
  isLoading = false,
  error = null,
  searchPlaceholder = "Search…",
  searchColumnId,
  exportFilename = "export.csv",
  emptyTitle = "No rows",
  emptyDescription = "Try adjusting filters or check that data has been ingested.",
  pageSize = 50,
  embedded = false,
}: DataTableProps<TData>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  const table = useReactTable({
    data,
    columns,
    state: { sorting, columnFilters, globalFilter, columnVisibility },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize } },
    globalFilterFn: "includesString",
  });

  const filteredRows = table.getFilteredRowModel().rows;
  const exportRows = useMemo(
    () =>
      filteredRows.map((row) => {
        const record: Record<string, unknown> = {};
        for (const cell of row.getVisibleCells()) {
          record[cell.column.id] = cell.getValue();
        }
        return record;
      }),
    [filteredRows],
  );

  const handleExport = (): void => {
    downloadCsv(exportFilename, exportRows);
  };

  if (isLoading) {
    return <Spinner className="py-24" label="Loading rows…" />;
  }

  if (error) {
    return (
      <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        {error}
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Input
          value={
            searchColumnId
              ? ((table.getColumn(searchColumnId)?.getFilterValue() as string) ?? "")
              : globalFilter
          }
          onChange={(event) => {
            const value = event.target.value;
            if (searchColumnId) {
              table.getColumn(searchColumnId)?.setFilterValue(value);
            } else {
              setGlobalFilter(value);
            }
          }}
          placeholder={searchPlaceholder}
          className="max-w-sm rounded-lg"
          aria-label="Search table"
        />

        <div className="flex items-center gap-2">
          <p className="text-sm text-[#94A3B8]">
            {filteredRows.length.toLocaleString()} row{filteredRows.length === 1 ? "" : "s"}
          </p>
          <Button
            variant="secondary"
            size="sm"
            className="rounded-lg"
            onClick={handleExport}
            disabled={filteredRows.length === 0}
          >
            Export CSV
          </Button>
        </div>
      </div>

      {filteredRows.length === 0 ? (
        <EmptyState title={emptyTitle} description={emptyDescription} />
      ) : (
        <div
          className={cn(
            "overflow-hidden bg-white",
            embedded
              ? "rounded-xl ring-1 ring-[#E8ECF0]"
              : "rounded-[var(--radius-lg)] border border-[var(--color-border)] shadow-[var(--shadow-sm)]",
          )}
        >
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse text-sm">
              <thead className="bg-[#F8FAFC]">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className="whitespace-nowrap border-b border-[#E8ECF0] px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-[#94A3B8]"
                      >
                        {header.isPlaceholder ? null : (
                          <button
                            type="button"
                            className={cn(
                              "inline-flex items-center gap-1",
                              header.column.getCanSort() &&
                                "cursor-pointer select-none hover:text-[var(--color-text)]",
                            )}
                            onClick={header.column.getToggleSortingHandler()}
                            disabled={!header.column.getCanSort()}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {{
                              asc: " ↑",
                              desc: " ↓",
                            }[header.column.getIsSorted() as string] ?? null}
                          </button>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    className="border-b border-[#E8ECF0] last:border-b-0 hover:bg-[#F8FAFC]/80"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="whitespace-nowrap px-4 py-3 text-[#334155]">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {filteredRows.length > 0 ? (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-[#94A3B8]">
            Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              className="rounded-lg"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              size="sm"
              className="rounded-lg"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
